import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPrint, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import LetterTemplate from '../../components/LetterTemplate/LetterTemplate';
import Modal from 'react-modal';
import ReactToPrint from 'react-to-print';

import './ManageRequest.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ManageRequest = () => {
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState({});
  const letterRefs = useRef({});

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/internship/requests`);
      setRequests(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/internship/deleteRequest/${id}`);
      setSelected((prev) => { const { [id]: _, ...rest } = prev; return rest; });
      fetchRequests();
    } catch (error) {
      console.error(error);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev[id]) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: true };
    });
  };

  const selectedCount = Object.keys(selected).length;
  const allSelected = requests.length > 0 && selectedCount === requests.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      requests.forEach((r) => { next[r.id] = true; });
      setSelected(next);
    }
  };

  const openPrintWindow = (items) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups for this site to print.');
      return;
    }

    const linkTags = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map(link => `<link rel="stylesheet" href="${link.href}">`)
      .join('');
    // CRA dev mode injects CSS as <style> tags via style-loader, not <link> tags
    const styleTags = Array.from(document.querySelectorAll('style'))
      .map(style => `<style>${style.textContent}</style>`)
      .join('');
    const stylesheets = linkTags + styleTags;

    const content = items
      .map(request => {
        const el = letterRefs.current[request.id];
        return el ? `<div class="print-page">${el.innerHTML}</div>` : '';
      })
      .filter(Boolean)
      .join('');

    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <base href="${window.location.origin}/">
    ${stylesheets}
    <style>
      @page { size: A4; margin: 0; }
      body { margin: 0; padding: 0; }
      .print-page { page-break-after: always; break-after: page; }
      .print-page:last-child { page-break-after: avoid; break-after: avoid; }
      .a4-outerspace { margin: 0; }
    </style>
  </head>
  <body>${content}</body>
</html>`);
    printWindow.document.close();

    let printed = false;
    const doPrint = () => {
      if (printed || printWindow.closed) return;
      printed = true;
      printWindow.focus();
      printWindow.print();
    };
    printWindow.onload = doPrint;
    // Fallback: onload may not fire on a document.write'd window in all browsers
    setTimeout(doPrint, 800);
  };

  const selectedRequests = requests.filter((r) => selected[r.id]);

  return (
    <div className='new-padding-q'>
      <div className='manage-header'>
        <h1>Internship Request Forms</h1>
        <div className='manage-header-spacer' />
        {requests.length > 0 && (
          <div className='manage-header-controls'>
            <label className='select-all-label'>
              <input
                type='checkbox'
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              Select All
            </label>
            <button
              className='btn green bulk-print-btn'
              disabled={selectedCount === 0}
              onClick={() => openPrintWindow(selectedRequests)}
            >
              Print Selected ({selectedCount}) <FontAwesomeIcon icon={faPrint} />
            </button>
            <button
              className='btn blue bulk-print-btn'
              onClick={() => openPrintWindow(requests)}
            >
              Print All ({requests.length}) <FontAwesomeIcon icon={faPrint} />
            </button>
          </div>
        )}
      </div>

      {requests.length > 0 && <hr className='letters-divider' />}

      {requests.length > 0 ? (
        requests.map((request) => (
          <div
            key={request.id}
            className={`request-card ${selected[request.id] ? 'request-card--selected' : ''}`}
          >
            <div className='card-checkbox-row'>
              <input
                type='checkbox'
                className='card-checkbox'
                checked={!!selected[request.id]}
                onChange={() => toggleSelect(request.id)}
              />
            </div>
            <div ref={(el) => { letterRefs.current[request.id] = el; }}>
              <LetterTemplate formData={request} />
            </div>
            <div className='ctrl-btn'>
              <button className='btn black' onClick={() => handleDelete(request.id)}>
                Delete <FontAwesomeIcon icon={faTrashAlt} />
              </button>
              <ReactToPrint
                trigger={() => (
                  <button className='btn blue'>
                    Print <FontAwesomeIcon icon={faPrint} />
                  </button>
                )}
                content={() => letterRefs.current[request.id]}
              />
            </div>
          </div>
        ))
      ) : (
        <p className='no-intern-data'>
          <FontAwesomeIcon className='info-circle' icon={faInfoCircle} />
          No internship request forms to be displayed.
        </p>
      )}

      <Modal
        isOpen={false}
        onRequestClose={() => {}}
        contentLabel='Modal'
        className='modal'
        overlayClassName='overlay'
      >
      </Modal>
    </div>
  );
};

export default ManageRequest;
